import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../utils/api';
import { useToast } from '../context/ToastContext';
import { UNIT_LABEL_SHORT } from '../constants/unitsMeta';
import {
  DynamicApplicationFields,
  initialValuesFromSchema,
  validateClient,
  buildResponsesPayload,
} from './DynamicApplicationFields';

function ApplicationForm() {
  const [schema, setSchema] = useState(null);
  const [schemaLoading, setSchemaLoading] = useState(true);
  const [schemaError, setSchemaError] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [acceptRules, setAcceptRules] = useState(false);
  const navigate = useNavigate();
  const { unit } = useParams();
  const base = `/${unit}`;
  const { success, error } = useToast();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSchemaLoading(true);
      setSchemaError(null);
      try {
        const data = await api.get(`/get-application-form-schema?unit=${unit}`);
        if (cancelled) return;
        const s = data.schema;
        setSchema(s);
        setFormValues(initialValuesFromSchema(s.fields));
      } catch (err) {
        if (!cancelled) {
          setSchemaError(err.message || 'Nie udało się załadować formularza');
          setSchema(null);
        }
      } finally {
        if (!cancelled) setSchemaLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [unit]);

  const handleFieldChange = (e) => {
    const { name, value, type, checked } = e.target;
    const v = type === 'checkbox' ? checked : value;
    setFormValues((prev) => ({ ...prev, [name]: v }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!schema?.fields) return;

    const fieldErrors = validateClient(schema.fields, formValues);
    if (!acceptRules) fieldErrors.accept_rules = 'Musisz zaakceptować regulamin';
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }

    const responses = buildResponsesPayload(schema.fields, formValues);

    setSubmitting(true);
    try {
      await api.post('/post-application', {
        unit,
        accept_rules: true,
        responses,
      });
      success('Podanie zostało wysłane! Wkrótce zostanie utworzony ticket na Discord.');
      navigate(`${base}/dashboard`);
    } catch (err) {
      console.error('Failed to submit application:', err);
      error(err.message || 'Nie udało się wysłać podania');
    } finally {
      setSubmitting(false);
    }
  };

  const unitLabel = UNIT_LABEL_SHORT[unit] || unit;

  if (schemaLoading) {
    return (
      <div className="form-page">
        <div className="form-container">
          <p className="loading">Ładowanie formularza…</p>
        </div>
      </div>
    );
  }

  if (schemaError || !schema) {
    return (
      <div className="form-page">
        <div className="form-container">
          <header className="form-header">
            <h1>Formularz rekrutacyjny</h1>
            <button type="button" onClick={() => navigate(`${base}/dashboard`)} className="btn-secondary">
              ← Powrót
            </button>
          </header>
          <p className="error-message" style={{ marginTop: '1rem' }}>
            {schemaError || 'Brak konfiguracji formularza.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="form-page">
      <div className="form-container">
        <header className="form-header">
          <h1>📝 Formularz rekrutacyjny – {unitLabel}</h1>
          <button type="button" onClick={() => navigate(`${base}/dashboard`)} className="btn-secondary">
            ← Powrót
          </button>
        </header>

        <form onSubmit={handleSubmit} className="application-form">
          <DynamicApplicationFields
            schemaFields={schema.fields}
            values={formValues}
            errors={errors}
            onChange={handleFieldChange}
          />

          <div className="form-group checkbox">
            <label>
              <input
                type="checkbox"
                name="accept_rules"
                checked={acceptRules}
                onChange={(e) => {
                  setAcceptRules(e.target.checked);
                  if (errors.accept_rules) setErrors((prev) => ({ ...prev, accept_rules: null }));
                }}
              />
              <span>Akceptuję regulamin serwera *</span>
            </label>
            {errors.accept_rules && <span className="error-message">{errors.accept_rules}</span>}
          </div>

          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? 'Wysyłanie...' : 'Wyślij podanie'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ApplicationForm;
